/**
 * "What changed this month": the board pack's bullets, written from figures by fixed rules.
 *
 * NOT A MODEL, NOT A SUMMARY. Each bullet is one rule applied to figures already on the pack, so a
 * reader can check every sentence against a number on the same page, and the same inputs always
 * produce the same words. No figure here is estimated, and a comparison that would mislead is
 * withheld rather than softened.
 *
 * THE RULES (each is implemented once, below, in this order):
 *
 *   1. SETTLEMENTS. State the month's settled count and value. Compare the value with the previous
 *      calendar month only when this month is COMPLETE (read from an export dated after it ended,
 *      remContract.ts PackMonth); a part month against a whole one always reads as a fall. The
 *      previous month is read the same way, from its own first export after it ended, so like is
 *      compared with like; the tab passes `previous` only then. Up is 'good', down is 'warn', equal
 *      is 'muted'. A percentage is given only when last month was above $0.
 *   2. SUBMISSIONS. The same rule on submissions (deals lodged with a lender), value compared,
 *      count stated.
 *   3. PIPELINE. Deals in flight and their value at this pack's export against the last export of
 *      the previous month. Both sides come from the same trend series, which is folded by the same
 *      function as the Pipeline tab, so they are like for like. No earlier export: say so.
 *   4. TOP BROKER. The broker with the highest settled value in the month, with their share of the
 *      month's settled value. A tie names everyone tied. Nothing settled: no bullet.
 *   5. TOP REFERRAL PARTNER. The partner with the most leads created in the month, from Leads &
 *      Partners. Only when that report answered for this month; never a guess.
 */

export interface PackFlow {
  count: number;
  valueCents: number;
}

export interface PackBulletInput {
  /** 'YYYY-MM'. */
  month: string;
  monthName: string;
  /** False when no export dated after the month ended exists yet, so the figures stop short of it. */
  complete: boolean;
  /** The month before, named in the part-month sentence even when it has no figures here. */
  previousMonthName: string;
  /** The last date the figures cover, already formatted for reading. */
  toLabel: string;
  settled: PackFlow;
  submissions: PackFlow;
  previous: { monthName: string; settled: PackFlow; submissions: PackFlow } | null;
  pipeline: { deals: number; valueCents: number } | null;
  previousPipeline: { dateLabel: string; deals: number; valueCents: number } | null;
  brokers: ReadonlyArray<{ name: string; settled: PackFlow }>;
  topPartner: { label: string; leads: number } | null;
}

export interface PackBullet {
  key: 'settlements' | 'submissions' | 'pipeline' | 'broker' | 'partner';
  tag: string;
  tone: 'good' | 'warn' | 'muted';
  text: string;
}

/** Whole dollars. The one conversion from cents on this module's output. */
export function dollars(cents: number): string {
  return (cents / 100).toLocaleString('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });
}

const plural = (n: number, one: string, many: string): string => `${n.toLocaleString('en-AU')} ${n === 1 ? one : many}`;

/** Rules 1 and 2 share this: one flow, stated, and compared only when that is fair. */
function flowBullet(
  key: 'settlements' | 'submissions',
  tag: string,
  verb: string,
  input: PackBulletInput,
  current: PackFlow,
  previous: PackFlow | null,
): PackBullet {
  const stated = `${dollars(current.valueCents)} ${verb} across ${plural(current.count, 'deal', 'deals')}`;
  if (!input.complete) {
    return {
      key, tag, tone: 'muted',
      text: `${stated} to ${input.toLabel}. ${input.monthName} is a part month until an export dated after it ends is published, so it is not compared with ${input.previousMonthName}.`,
    };
  }
  if (!input.previous || !previous) {
    return { key, tag, tone: 'muted', text: `${stated} in ${input.monthName}. No earlier month to compare with.` };
  }
  const diff = current.valueCents - previous.valueCents;
  if (diff === 0) {
    return { key, tag, tone: 'muted', text: `${stated} in ${input.monthName}, level with ${input.previous.monthName}.` };
  }
  const share = previous.valueCents > 0 ? ` (${Math.round((Math.abs(diff) / previous.valueCents) * 100)}%)` : '';
  return {
    key, tag,
    tone: diff > 0 ? 'good' : 'warn',
    text: `${stated} in ${input.monthName}, ${diff > 0 ? 'up' : 'down'} ${dollars(Math.abs(diff))}${share} on ${input.previous.monthName} (${dollars(previous.valueCents)}).`,
  };
}

export function buildPackBullets(input: PackBulletInput): PackBullet[] {
  const out: PackBullet[] = [];

  // Rule 1.
  out.push(flowBullet('settlements', 'Settlements', 'settled', input, input.settled, input.previous?.settled ?? null));
  // Rule 2.
  out.push(flowBullet('submissions', 'Submissions', 'submitted', input, input.submissions, input.previous?.submissions ?? null));

  // Rule 3.
  if (input.pipeline) {
    const now = `${plural(input.pipeline.deals, 'deal', 'deals')} in flight worth ${dollars(input.pipeline.valueCents)}`;
    if (!input.previousPipeline) {
      out.push({ key: 'pipeline', tag: 'Pipeline', tone: 'muted', text: `${now}. No export from the month before to compare with.` });
    } else {
      const diff = input.pipeline.valueCents - input.previousPipeline.valueCents;
      out.push({
        key: 'pipeline', tag: 'Pipeline',
        tone: diff > 0 ? 'good' : diff < 0 ? 'warn' : 'muted',
        text: diff === 0
          ? `${now}, level with the ${input.previousPipeline.dateLabel} export.`
          : `${now}, ${diff > 0 ? 'up' : 'down'} ${dollars(Math.abs(diff))} on the ${input.previousPipeline.dateLabel} export (${plural(input.previousPipeline.deals, 'deal', 'deals')}, ${dollars(input.previousPipeline.valueCents)}).`,
      });
    }
  }

  // Rule 4.
  const top = topSettlers(input.brokers, input.settled.valueCents);
  if (top) {
    const { names: leaders, valueCents: best, sharePercent: share } = top;
    const who = leaders.length === 1 ? `${leaders[0]} settled` : `${leaders.join(' and ')} each settled`;
    out.push({
      key: 'broker', tag: 'Brokers', tone: 'muted',
      text: `${who} the most: ${dollars(best)}${share !== null ? `, ${share}% of the month's settled value` : ''}.`,
    });
  }

  // Rule 5.
  if (input.topPartner && input.topPartner.leads > 0) {
    out.push({
      key: 'partner', tag: 'Referrals', tone: 'muted',
      text: `${input.topPartner.label} referred the most leads created in ${input.monthName}: ${plural(input.topPartner.leads, 'lead', 'leads')}.`,
    });
  }

  return out;
}

/**
 * Rule 4's leaders: the broker (or brokers, on a tie) with the highest settled value, with their
 * share of the month's settled value. Null when nobody settled. Shared by the bullet and the
 * pack's leader card, so the two can never name different people.
 */
export function topSettlers(
  brokers: ReadonlyArray<{ name: string; settled: PackFlow }>,
  monthSettledCents: number,
): { names: string[]; valueCents: number; sharePercent: number | null } | null {
  const best = Math.max(0, ...brokers.map((b) => b.settled.valueCents));
  if (best <= 0) return null;
  return {
    names: brokers.filter((b) => b.settled.valueCents === best).map((b) => b.name).sort(),
    valueCents: best,
    sharePercent: monthSettledCents > 0 ? Math.round((best / monthSettledCents) * 100) : null,
  };
}

// ------------------------------------------------------------ comparison cards

/**
 * "What changed this month" as cards: this month against the month before and against the same
 * month a year earlier (founder, 5 Oct 2026). The same rules as the bullets, applied to two
 * baselines instead of one:
 *
 *   - FLOW (settlements, submissions) is compared only when this month is complete and the
 *     baseline month was read from its OWN first export after it ended. A part month is never
 *     compared with a whole one. A percentage is given only when the baseline was above $0.
 *   - STOCK (pipeline) compares the trend series at this month's last export with the series at
 *     the baseline month's last export, as rule 3 does.
 *   - A baseline that cannot be read is never invented: the card says why, in a few words
 *     ("No Aug 2025 export"), and draws no delta.
 *
 * The tab decides which baselines exist (it alone sees the exports); this module only does the
 * arithmetic and the wording, so the same inputs always give the same cards.
 */

export type Baseline =
  | { status: 'ok'; month: string; flow: PackFlow }
  | { status: 'unavailable'; month: string; reason: string };

export interface PackDelta {
  status: 'ok';
  /** 'YYYY-MM' of the baseline. */
  month: string;
  base: PackFlow;
  diffCents: number;
  /** Whole percent of the baseline value, or null when the baseline was $0. */
  percent: number | null;
  direction: 'up' | 'down' | 'level';
}

export type PackComparisonSide = PackDelta | { status: 'unavailable'; month: string; reason: string };

export interface PackComparison {
  key: 'settlements' | 'submissions' | 'pipeline';
  label: string;
  current: PackFlow;
  /** What `current.count` counts, for the card's sub-line. */
  countNoun: { one: string; many: string };
  vsPreviousMonth: PackComparisonSide;
  vsLastYear: PackComparisonSide;
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2025-08' to 'Aug 2025'. A fixed list, not the locale, so the wording is the same in every browser. */
export function monthShortYear(month: string): string {
  const [y, m] = month.split('-').map(Number);
  if (!y || !m || m < 1 || m > 12) return month;
  return `${SHORT_MONTHS[m - 1]} ${y}`;
}

/** The same month a year earlier. */
export function sameMonthLastYear(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return `${y - 1}-${String(m).padStart(2, '0')}`;
}

export function compareWith(current: PackFlow, baseline: Baseline): PackComparisonSide {
  if (baseline.status !== 'ok') return baseline;
  const diffCents = current.valueCents - baseline.flow.valueCents;
  return {
    status: 'ok',
    month: baseline.month,
    base: baseline.flow,
    diffCents,
    percent: baseline.flow.valueCents > 0 ? Math.round((Math.abs(diffCents) / baseline.flow.valueCents) * 100) : null,
    direction: diffCents > 0 ? 'up' : diffCents < 0 ? 'down' : 'level',
  };
}

export interface PackComparisonInput {
  settled: PackFlow;
  submissions: PackFlow;
  /** Flow baselines: each side's settled and submissions, or why it is not compared. */
  previousFlow: { month: string; settled: PackFlow; submissions: PackFlow } | { month: string; reason: string };
  lastYearFlow: { month: string; settled: PackFlow; submissions: PackFlow } | { month: string; reason: string };
  /** Null when the trend series has no point at this pack's export: no pipeline card. */
  pipeline: PackFlow | null;
  previousPipeline: Baseline;
  lastYearPipeline: Baseline;
}

const flowSide = (side: PackComparisonInput['previousFlow'], pick: 'settled' | 'submissions'): Baseline =>
  'reason' in side ? { status: 'unavailable', month: side.month, reason: side.reason } : { status: 'ok', month: side.month, flow: side[pick] };

export function buildPackComparisons(input: PackComparisonInput): PackComparison[] {
  const out: PackComparison[] = [
    {
      key: 'settlements',
      label: 'Settlements',
      current: input.settled,
      countNoun: { one: 'deal settled', many: 'deals settled' },
      vsPreviousMonth: compareWith(input.settled, flowSide(input.previousFlow, 'settled')),
      vsLastYear: compareWith(input.settled, flowSide(input.lastYearFlow, 'settled')),
    },
    {
      key: 'submissions',
      label: 'Submissions',
      current: input.submissions,
      countNoun: { one: 'deal lodged', many: 'deals lodged' },
      vsPreviousMonth: compareWith(input.submissions, flowSide(input.previousFlow, 'submissions')),
      vsLastYear: compareWith(input.submissions, flowSide(input.lastYearFlow, 'submissions')),
    },
  ];
  if (input.pipeline) {
    out.push({
      key: 'pipeline',
      label: 'Pipeline',
      current: input.pipeline,
      countNoun: { one: 'deal in flight', many: 'deals in flight' },
      vsPreviousMonth: compareWith(input.pipeline, input.previousPipeline),
      vsLastYear: compareWith(input.pipeline, input.lastYearPipeline),
    });
  }
  return out;
}

/** A delta in words, for a screen reader and a hover title: "Up $50,000 (50%) on July 2026 ($100,000)". */
export function deltaSentence(side: PackDelta, monthName: string): string {
  if (side.direction === 'level') return `Level with ${monthName} (${dollars(side.base.valueCents)})`;
  const pct = side.percent !== null ? ` (${side.percent}%)` : '';
  return `${side.direction === 'up' ? 'Up' : 'Down'} ${dollars(Math.abs(side.diffCents))}${pct} on ${monthName} (${dollars(side.base.valueCents)})`;
}
